import { create } from 'zustand'

export type ToastVariant = 'default' | 'destructive' | 'success'

export interface ToastMessage {
  id: string
  title: string
  description?: string
  variant: ToastVariant
  durationMs: number
}

export type ToastInput = Omit<ToastMessage, 'id' | 'variant' | 'durationMs'> &
  Partial<Pick<ToastMessage, 'variant' | 'durationMs'>>

interface ToastState {
  toasts: ToastMessage[]
  push: (input: ToastInput) => string
  dismiss: (id: string) => void
  clear: () => void
}

const MAX_VISIBLE = 4

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],
  push: (input) => {
    const id = crypto.randomUUID()
    const message: ToastMessage = {
      variant: 'default',
      durationMs: 5000,
      ...input,
      id,
    }
    set((state) => ({ toasts: [...state.toasts, message].slice(-MAX_VISIBLE) }))
    return id
  },
  dismiss: (id) => {
    set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) }))
  },
  clear: () => {
    set({ toasts: [] })
  },
}))

/**
 * Imperative toast API — safe to call from non-React code such as the Axios
 * interceptor, router loaders or error boundaries.
 */
export const toast = {
  show: (input: ToastInput) => useToastStore.getState().push(input),
  success: (title: string, description?: string) =>
    useToastStore
      .getState()
      .push({ title, variant: 'success', ...(description && { description }) }),
  error: (title: string, description?: string) =>
    useToastStore
      .getState()
      .push({ title, variant: 'destructive', ...(description && { description }) }),
  dismiss: (id: string) => {
    useToastStore.getState().dismiss(id)
  },
}
